/**
 * Brand → Duro theme. Maps a site's generated theme-engine Tokens onto Duro's
 * CSS-var contract, so real @duro-app/ui components render in the user's Brand —
 * in the editor (live) and in the SSR'd publish output.
 *
 * Follows the application-landscape `generate-duro-theme` pattern: @duro-app/tokens
 * exports every token as its hashed CSS-var ref (`colors.bg === "var(--bg-…)"`),
 * so we read the var NAMES from the package itself — no hardcoded hashes, survives
 * Duro bumps. Unlike that app's single static theme, ours is computed per site at
 * runtime from the user's Brand. Only Brand-driven roles are overridden; Duro's
 * semantic feedback colours (error/success/warning/info) stay as Duro ships them.
 */
// Barrel on purpose: the deep `@duro-app/tokens/tokens/*.css` subpaths resolve
// to the package's UNCOMPILED `src/*.css.ts`, which call `css.defineVars()`
// at import time and throw unless the StyleX babel plugin runs over them. The
// app's vite and vitest configs now run it (vite-stylex.ts), but this module
// only reads the tokens as data (var refs), feeds the SSR'd publish output,
// and must stay importable from any runtime, pipeline or not — so the
// barrel's prebuilt `dist` is the correct entry here. Styling code
// (css.create) deep-imports.
// eslint-disable-next-line duro/no-tokens-barrel-import
import { colors, typography } from "@duro-app/tokens";
import { fontStack, type Tokens } from "@website-builder/theme-engine";
import { FLUID_TYPE_VARS } from "./fluidType.js";

/** `"var(--bg-xqkwqtp)"` → `"--bg-xqkwqtp"`. */
function varName(ref: string): string {
  const m = /^var\((--[A-Za-z0-9-]+)\)$/.exec(ref);
  if (!m) throw new Error(`unexpected Duro token ref: ${ref}`);
  return m[1];
}

/** The Brand-driven override map: Duro custom-property name → value. */
export function brandToDuroVars(t: Tokens): Record<string, string> {
  const out: Record<string, string> = {};
  const set = (ref: string, value: string) => {
    out[varName(ref)] = value;
  };
  // Surfaces
  set(colors.bg, t.color.background);
  set(colors.bgCard, t.color.muted);
  set(colors.bgCardHover, t.color.muted);
  // Text
  set(colors.text, t.color.foreground);
  set(colors.textMuted, t.color.mutedForeground);
  // Accent = the brand's primary action colour
  set(colors.accent, t.color.primarySolid);
  set(colors.accentHover, t.color.primary["700"]);
  set(colors.accentContrast, t.color.primaryForeground);
  // Lines
  set(colors.border, t.color.border);
  // Type — brand font family, plus the fluid type scale (font-size + leading
  // ramp mobile→desktop, keyed on --wb-vw). Brand-independent, so merged in
  // wholesale; see fluidType.ts.
  set(typography.fontFamily, fontStack(t.typography.fontFamily.body));
  Object.assign(out, FLUID_TYPE_VARS);
  return out;
}

/** Render the override map as a scoped CSS rule (for the published stylesheet). */
export function duroVarsToCss(
  vars: Record<string, string>,
  selector = ":root",
): string {
  const body = Object.entries(vars)
    .map(([k, v]) => `${k}:${v}`)
    .join(";");
  return `${selector}{${body}}`;
}

/** The override map as a React inline-style object (for the editor canvas scope). */
export function brandToDuroStyle(t: Tokens): Record<string, string> {
  return brandToDuroVars(t);
}
